import React from 'react';
import { Card, Descriptions, Tag, Button, Row, Col, Statistic } from 'antd';
import {
  EditOutlined,
  CalendarOutlined,
  DollarOutlined,
  PercentageOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { CreditCardResponse2 } from '../../model/Credit-card.model';

interface CreditCardInfoProps {
  creditCard: CreditCardResponse2;
  onRefresh: () => void;
}

const CreditCardInfo: React.FC<CreditCardInfoProps> = ({
  creditCard,
  onRefresh,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      TRA_THE: { color: 'blue', text: 'Trả thẻ' },
      LUU_THE: { color: 'green', text: 'Lưu thẻ' },
      DANG_LAM: { color: 'orange', text: 'Đang làm' },
      DANG_CHO: { color: 'blue', text: 'Đang chờ' },
      KHONG_LAM: { color: 'gray', text: 'Không làm' },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return config ? (
      <Tag color={config.color}>{config.text}</Tag>
    ) : (
      <Tag>{status}</Tag>
    );
  };

  const getCtvInfo = () => {
    const customer = creditCard.customer;
    if (!customer) return null;

    // Ưu tiên thông tin partner trực tiếp
    let partnerName = customer.partnerName;
    let partnerCode = customer.partnerCode;
    let commissionRate = customer.commissionRateForPartner;

    // Nếu không có thông tin trực tiếp, lấy từ object partner
    if (!partnerName && !partnerCode && customer.partner) {
      partnerName = customer.partner.name;
      partnerCode = customer.partner.email; // Sử dụng email làm code
    }

    if (partnerName || partnerCode) {
      return {
        name: partnerName || partnerCode || 'Không rõ',
        rate: commissionRate || 0,
        display: partnerName
          ? `${partnerName} (${commissionRate || 0}%)`
          : `${partnerCode} (${commissionRate || 0}%)`,
      };
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Quick Statistics */}
      <Card size="small" className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <Row gutter={16}>
          <Col span={5}>
            <Statistic
              title="Hạn mức"
              value={creditCard.creditLimit || 0}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined className="text-blue-500" />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Phí mặc định"
              value={creditCard.defaultFeeRate || 0}
              suffix="%"
              prefix={<PercentageOutlined className="text-green-500" />}
            />
          </Col>

          <Col span={5}>
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">CTV & Phí</div>
              <div className="flex flex-col space-y-1">
                {getCtvInfo() ? (
                  <>
                    <div className="text-sm font-medium text-purple-600 flex items-center justify-center">
                      <UserOutlined className="mr-1" />
                      {getCtvInfo()?.name}
                    </div>
                    <div className="text-xs text-purple-500">
                      Phí: {getCtvInfo()?.rate}%
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400">Chưa có CTV</div>
                )}
              </div>
            </div>
          </Col>
          <Col span={5}>
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Trạng thái</div>
              <div className="flex flex-col space-y-1">
                {getStatusTag(creditCard.status || '')}
                {getStatusTag(creditCard.newStatus || '')}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Detailed Information */}
      <Card title="Thông tin chi tiết" size="small">
        <Descriptions
          column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
          bordered
          size="small"
        >
          <Descriptions.Item label="Số thẻ">
            <span className="font-mono">{creditCard.cardNumber}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Tên chủ thẻ">
            {creditCard.cardHolderName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Tên khách hàng">
            {creditCard.customer?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Loại thẻ">
            {creditCard.cardType?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Ngân hàng">
            {creditCard.bank?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày hết hạn">
            {formatDate(creditCard.expiryDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            {getStatusTag(creditCard.status || '')}
          </Descriptions.Item>
          <Descriptions.Item label="Hoạt động">
            {getStatusTag(creditCard.newStatus || '')}
          </Descriptions.Item>
          <Descriptions.Item label="Khả dụng">
            <Tag color={creditCard.isActive ? 'green' : 'red'}>
              {creditCard.isActive ? 'Hoạt động' : 'Không hoạt động'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Hạn mức tín dụng">
            {formatCurrency(creditCard.creditLimit || 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Phí mặc định">
            {creditCard.defaultFeeRate ? `${creditCard.defaultFeeRate}%` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày đến hạn hàng tháng">
            {creditCard.cyclePaymentDate || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="CTV">
            {getCtvInfo() ? (
              <div className="flex items-center">
                <UserOutlined className="text-purple-500 mr-2" />
                <span className="font-medium text-purple-600">
                  {getCtvInfo()?.name}
                </span>
              </div>
            ) : (
              <span className="text-gray-400">Chưa có CTV</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Phí CTV">
            {getCtvInfo() ? (
              <span className="font-medium text-purple-600">
                {getCtvInfo()?.rate}%
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {formatDate(creditCard.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Cập nhật lần cuối">
            {formatDate(creditCard.updatedAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Ghi chú" span={2}>
            {creditCard.notes || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="ID thẻ">
            <span className="font-mono text-gray-600">#{creditCard.id}</span>
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default CreditCardInfo;
